// @ts-check
import React, { useContext } from 'react';

import { AuthContext, AuthContextType } from 'lib/auth/AuthContext';
import { Button, ButtonSize, ButtonType, ButtonVariant } from 'components/Button';
import { ColorBox } from 'components/sections/ColorBox';
import { ControlHero } from 'components/sections/ControlHero';
import { HowItWorks } from 'components/sections/HowItWorks';
import { HowToCards } from 'components/sections/HowToCards';
import { Image } from 'components/sections/Image';
import { Layout } from 'components/Layout';
import { Gutter } from 'components/Gutter';
import { List } from 'components/List';
import { ListItemSize } from 'components/List/ListItem';
import { Magazine, MagazineArticleCard } from 'components/sections/Magazine';
import { Reviews } from 'components/sections/Reviews';
import { Usp } from 'components/sections/Usp';
import { arrowRight } from 'icons/default';
import { colorBookTime } from 'content/colorBox';
import { controlHeroBookTime } from 'content/controlHero';
import { howItWorksItemsBuy } from 'content/howItWorks';
import { howToCardsBuy } from 'content/howToCards';
import { reviews } from 'content/reviews';
import { Colors } from 'enums/Colors';
import NordeaFinance from 'public/assets/logotype/nordeafinance.svg';
import useMediaQuery from 'utils/useMediaQuery';

import styles from '../BuyUsedCar/BuyUsedCar.module.scss';

interface Props {
  articles: MagazineArticleCard[];
}

export const BookTime: React.FC<Props> = ({ articles }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { isLoggedIn } = useContext(AuthContext) as AuthContextType;

  return (
    <Layout>
      <div className={styles.wrapper}>
        <ControlHero
          title={controlHeroBookTime.title}
          text={controlHeroBookTime.text}
          controlTitle={controlHeroBookTime.controlTitle}
          controlButtonText={isLoggedIn ? 'Boka tid med Blipp' : 'Logga in och boka tid'}
          mobileSrc={controlHeroBookTime.mobileSrc}
          desktopSrc={controlHeroBookTime.desktopSrc}
        />
        <Gutter size="negative">
          <ColorBox
            color={Colors.MintSweet}
            title={colorBookTime.title}
            text={colorBookTime.text}
            list={colorBookTime.list}
            buttons={colorBookTime.buttons}
          />
        </Gutter>
        <Gutter size="medium">
          <HowToCards
            title={howToCardsBuy.title}
            howToItems={howToCardsBuy.howToItems}
            hideImages
            showBg
            showIcons
          />
        </Gutter>

        <Gutter size="large">
          <HowItWorks
            heading="Alltid trygg med Blipp-garantin"
            imageSrc="/assets/images/child-sleeping.jpg"
            color={Colors.DarkBlue}
            alt="Sovande barn i bil"
          >
            <p>
              Slipp oroa dig för oförutsedda problem. När du tecknar en Blipp-garanti för din bil,
              motorcykel eller husbil hjälper dig om något händer, så du kan tänka på annat.
            </p>
            <strong>Blipp-garantin innefattar bland annat:</strong>
            <div className={styles['how-it-works-list']}>
              <List
                size={ListItemSize.Normal}
                items={howItWorksItemsBuy}
                bgColor={Colors.Transparent}
                listItemLabelColor={Colors.StoneGrey100}
              />
            </div>
          </HowItWorks>
        </Gutter>
        <Gutter size="xlarge">
          <HowItWorks
            heading="Ett lån med bilen som säkerhet."
            imageSrc="/assets/images/woman-reading-in-trunk.jpg"
            color={Colors.DarkBlue}
            alt="Kvinna läser bok i bakluckan på en bil."
          >
            <p>
              Det ska vara enkelt att äga. Därför erbjuder vi tillsammans med Nordea Finance ett
              fordonslån med samma ränta för alla,{' '}
              <span className="textHighlight">
                {process.env.NEXT_PUBLIC_LOAN_INTEREST?.replace('.', ',')}%
              </span>
              , när du köper via Blipp.
            </p>
            <NordeaFinance />
            <div className={styles['how-it-works-button']}>
              <Button
                type={ButtonType.Button}
                href="/lanelofte-billan"
                size={ButtonSize.Large}
                trailingIcon={arrowRight}
                variant={ButtonVariant.GhostInverted}
              >
                Läs mer om fordonslån
              </Button>
            </div>
          </HowItWorks>
        </Gutter>
        <Gutter size="xlarge">
          <Reviews title={reviews.title} reviews={reviews.reviews} />
        </Gutter>

        <Gutter size="xlarge">
          <Usp soldCars={2645} />
        </Gutter>
        <Gutter size="large">
          {isMobile ? (
            <Image
              rounded
              src="/assets/images/my-ride-my-rules-mobile.jpg"
              alt="An electric car and two people in front of a wooden house"
              title="My ride. My rules."
              intro="Att ha en egen bil ska göra dig fri, inte begränsad."
            />
          ) : (
            <Image
              rounded
              src="/assets/images/my-ride-my-rules-desktop.jpg"
              alt="An electric car and two people in front of a wooden house"
              title="My ride. My rules."
              intro="Att ha en egen bil ska göra dig fri, inte begränsad."
            />
          )}
        </Gutter>
        <Gutter size="large">
          <Magazine articles={articles} />
        </Gutter>
      </div>
    </Layout>
  );
};
